import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/services/api'
import toast from 'react-hot-toast'

// Tipos
interface Escritorio {
  id: string
  nome: string
  plano: 'STARTER' | 'BUSINESS' | 'ENTERPRISE'
}

interface Usuario {
  id: string
  nome: string
  email: string
  nivel: 'ADMIN' | 'OPERADOR' | 'VISUALIZADOR'
  escritorio_id: string
  escritorio?: Escritorio
  avatar_url?: string
}

interface AuthContextType {
  usuario: Usuario | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, senha: string) => Promise<void>
  logout: () => void
  atualizarUsuario: (dados: Partial<Usuario>) => void
}

// Storage keys
const TOKEN_KEY = 'MONITOR_token'
const REFRESH_TOKEN_KEY = 'MONITOR_refresh_token'
const USUARIO_KEY = 'MONITOR_usuario'

// Context
const AuthContext = createContext<AuthContextType>({} as AuthContextType)

// Provider
export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  // Carregar usuário do localStorage ao iniciar
  useEffect(() => {
    const carregarUsuario = async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY)
        const usuarioSalvo = localStorage.getItem(USUARIO_KEY)

        if (token && usuarioSalvo) {
          // Configurar token no axios
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`

          // Tentar validar token com o backend
          try {
            const response = await api.get('/auth/me')
            setUsuario(response.data.data)
            localStorage.setItem(USUARIO_KEY, JSON.stringify(response.data.data))
          } catch (error) {
            // Token inválido, tentar refresh
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
            if (refreshToken) {
              try {
                const refreshResponse = await api.post('/auth/refresh', { refreshToken })
                const { accessToken, usuario: novoUsuario } = refreshResponse.data.data

                localStorage.setItem(TOKEN_KEY, accessToken)
                localStorage.setItem(USUARIO_KEY, JSON.stringify(novoUsuario))
                api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

                setUsuario(novoUsuario)
              } catch {
                // Refresh também falhou, fazer logout
                limparDados()
              }
            } else {
              limparDados()
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar usuário:', error)
        limparDados()
      } finally {
        setIsLoading(false)
      }
    }

    carregarUsuario()
  }, [])

  // Limpar dados de autenticação
  const limparDados = () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USUARIO_KEY)
    delete api.defaults.headers.common['Authorization']
    setUsuario(null)
  }

  // Login
  const login = async (email: string, senha: string) => {
    try {
      const response = await api.post('/auth/login', { email, senha })
      const { accessToken, refreshToken, usuario: usuarioLogado } = response.data.data

      // Salvar no localStorage
      localStorage.setItem(TOKEN_KEY, accessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
      localStorage.setItem(USUARIO_KEY, JSON.stringify(usuarioLogado))

      // Configurar axios
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

      // Atualizar estado
      setUsuario(usuarioLogado)

      // Toast de sucesso
      toast.success(`Bem-vindo, ${usuarioLogado.nome}!`)

      // Navegar para dashboard
      navigate('/')
    } catch (error: any) {
      const mensagem = error.response?.data?.error || 'Erro ao fazer login'
      toast.error(mensagem)
      throw error
    }
  }

  // Logout
  const logout = async () => {
    try {
      // Tentar invalidar token no backend
      await api.post('/auth/logout').catch(() => {})
    } finally {
      limparDados()
      toast.success('Sessão encerrada')
      navigate('/login')
    }
  }

  // Atualizar dados do usuário
  const atualizarUsuario = (dados: Partial<Usuario>) => {
    if (usuario) {
      const novoUsuario = { ...usuario, ...dados }
      setUsuario(novoUsuario)
      localStorage.setItem(USUARIO_KEY, JSON.stringify(novoUsuario))
    }
  }

  return (
    <AuthContext.Provider
      value={{
        usuario,
        isAuthenticated: !!usuario,
        isLoading,
        login,
        logout,
        atualizarUsuario,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

export default AuthContext

